import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Confetti } from "@/components/confetti";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import AuthStore from "../../../stores/AuthStore";
import { AppLayout } from "../shared/layout";
import { CountUp } from "@/components/count-up";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Flame,
  MessageCircle,
  Sparkles,
  ShieldAlert,
  BookOpen,
  TrendingUp,
  ArrowRight,
  RotateCcw,
} from "lucide-react";
import {
  getPracticeSessions,
  getScenarios,
  getCoachProfile,
  updateCoachProfile,
  generateCoachPlan,
} from "@/data/requests/practice-requests";
import { applyStatusToPersonal, PENDING_STATUS_KEY } from "@/lib/personal-plan";

function overallOf(s) {
  if (!s.feedback) return null;
  return Math.round(
    (s.feedback.clarityScore + s.feedback.empathyScore + s.feedback.boundaryScore) / 3
  );
}

function computeStreak(sessions) {
  const days = new Set(sessions.map((s) => new Date(s.startedAt).toDateString()));
  let streak = 0;
  const d = new Date();
  if (!days.has(d.toDateString())) d.setDate(d.getDate() - 1);
  while (days.has(d.toDateString())) {
    streak += 1;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

// Category accents keep the dashboard lively while indigo anchors the primary action.
const ACTIONS = [
  { key: "practice", to: "/practice", icon: MessageCircle, accent: "#1E293B" },
  { key: "redFlag", to: "/red-flag-check", icon: ShieldAlert, accent: "#F04461", bg: "#FFF0F3" },
  { key: "journal", to: "/journal", icon: BookOpen, accent: "#159C78", bg: "#E9FBF5" },
  { key: "quiz", to: "/quiz", icon: Sparkles, accent: "#F59E0B", bg: "#FFF6DE" },
  { key: "progress", to: "/progress", icon: TrendingUp, accent: "#5865F2", bg: "#EEF0FF" },
];

const WARM = "#FF5F65";
const INDIGO = "#5865F2";

export function HomeScreen() {
  const { t, i18n } = useTranslation();
  const user = AuthStore.user;

  const { data: sessionsData } = useQuery({
    queryKey: ["practice-sessions"],
    queryFn: getPracticeSessions,
  });
  const { data: scenariosData } = useQuery({
    queryKey: ["scenarios"],
    queryFn: getScenarios,
  });

  const scenarioCount = scenariosData?.data?.length || 0;

  const stats = useMemo(() => {
    const sessions = sessionsData?.data || [];
    const completed = sessions.filter((s) => s.status === "COMPLETED" && s.feedback);
    const scored = completed.map(overallOf).filter((v) => v !== null);
    const avg = scored.length
      ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
      : null;
    return {
      streak: computeStreak(sessions),
      sessions: completed.length,
      avg,
    };
  }, [sessionsData]);

  const [celebrate, setCelebrate] = useState(false);
  const celebratedRef = useRef(false);
  useEffect(() => {
    const milestones = [3, 7, 14, 30, 50, 100, 365];
    if (!celebratedRef.current && milestones.includes(stats.streak)) {
      celebratedRef.current = true;
      setCelebrate(true);
      const id = setTimeout(() => setCelebrate(false), 2600);
      return () => clearTimeout(id);
    }
  }, [stats.streak]);

  const { data: coachData } = useQuery({ queryKey: ["coach-profile"], queryFn: getCoachProfile });
  const coach = coachData?.data || {};
  // A status suggestion the user never answered on the feedback screen —
  // re-ask once here, then clear it either way.
  const queryClient = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingBusy, setPendingBusy] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_STATUS_KEY);
      if (!raw) return setPendingStatus(null);
      const { status } = JSON.parse(raw);
      if (!status || status === coach.personal?.status) {
        localStorage.removeItem(PENDING_STATUS_KEY);
        return setPendingStatus(null);
      }
      setPendingStatus(status);
    } catch {}
  }, [coach.personal?.status]);

  const answerPendingStatus = async (accept) => {
    if (pendingBusy) return;
    try {
      setPendingBusy(true);
      if (accept) {
        const updated = applyStatusToPersonal(coach.personal, pendingStatus);
        await updateCoachProfile({ personal: updated, situationNotes: "" });
        try { await generateCoachPlan(i18n.language); } catch {}
        queryClient.invalidateQueries({ queryKey: ["coach-profile"] });
      }
      localStorage.removeItem(PENDING_STATUS_KEY);
      setPendingStatus(null);
    } catch {
    } finally {
      setPendingBusy(false);
    }
  };

  const sessions = sessionsData?.data || [];
  const activeSession = sessions.find((s) => s.status === "ACTIVE");
  const statPills = [
    { icon: Flame, value: stats.streak, suffix: "", label: t("dash.statStreak"), color: WARM, bg: "#FFF0F3" },
    { icon: MessageCircle, value: stats.sessions, suffix: "", label: t("dash.statSessions"), color: "#159C78", bg: "#E9FBF5" },
    {
      icon: Sparkles,
      value: stats.avg !== null ? stats.avg : "—",
      suffix: "/10",
      label: t("dash.statAvg"),
      color: INDIGO,
      bg: "#EEF0FF",
    },
  ];

  return (
    <AppLayout
      title="Dashboard"
      description={`${t("dash.welcome")}${user?.name ? `, ${user.name}` : ""}`}
      hideHeader
    >
      <div
        className="relative overflow-hidden px-0.5 pb-5 pt-1 md:px-3 md:pb-7"
        style={{
          background:
            "radial-gradient(circle at 92% 5%, rgba(255,95,101,0.09), transparent 27%), radial-gradient(circle at 8% 2%, rgba(88,101,242,0.09), transparent 29%), linear-gradient(180deg, #F7F9FF 0%, #FFFFFF 48%, #FFF7F8 100%)",
        }}
      >
        {celebrate && <Confetti />}
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#FF5F65]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-[#5865F2]/10 blur-3xl" />

        <div className="relative mb-6 flex items-center justify-between gap-5">
          <div>
            <p className="mb-1 text-xs font-bold tracking-wide text-muted-foreground">
              {t("dash.welcome")}{user?.name ? `, ${user.name}` : ""}
            </p>
            <h1 className="text-3xl font-extrabold leading-tight text-[#1E293B] md:text-[2.6rem]">
              {t("dash.heroTitle")} <span className="text-[#5865F2]">{t("dash.heroHighlight")}</span>
            </h1>
          </div>
          <div className="hidden h-14 w-14 rotate-[-7deg] items-center justify-center rounded-2xl bg-[#E5E8FF] text-[#5865F2] sm:flex">
            <Sparkles className="h-6 w-6" />
          </div>
        </div>

        <div className="relative mb-4 space-y-3">
          {pendingStatus && coach.personal && (
            <Card className="border-0 p-4 shadow-sm">
              <p className="text-sm font-semibold">
                {t(`practiceSession.statusChange.lead.${pendingStatus}`, { defaultValue: t("practiceSession.statusChange.lead.generic") })}
              </p>
              <div className="mt-3 flex items-center gap-4">
                <Button size="sm" disabled={pendingBusy} onClick={() => answerPendingStatus(true)}>{t("practiceSession.statusChange.yes")}</Button>
                <button className="text-sm font-medium text-muted-foreground" disabled={pendingBusy} onClick={() => answerPendingStatus(false)}>{t("practiceSession.statusChange.no")}</button>
              </div>
            </Card>
          )}

          {coachData && !coach.personal && (
            <Link to="/personal-setup" className="flex items-center gap-3 rounded-2xl bg-[#1E293B] p-3 text-white shadow-lg shadow-slate-900/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"><Sparkles className="h-5 w-5" /></div>
              <span className="flex-1 text-sm font-bold">{t("dash.personalBanner.title")}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-action px-3 py-2 text-xs font-bold text-white">{t("dash.personalBanner.cta")} <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          )}

          {activeSession && (
            <Link to={`/practice/${activeSession.id}`} className="flex items-center gap-3 rounded-2xl bg-[#1E293B] p-3 text-white shadow-lg shadow-slate-900/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"><RotateCcw className="h-5 w-5" /></div>
              <span className="flex-1 text-sm font-bold">{t("dash.continueTitle")}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-action px-3 py-2 text-xs font-bold text-white">{t("dash.continueCta")} <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          )}
        </div>

        <div className="relative grid gap-3 lg:grid-cols-[minmax(0,1.55fr)_minmax(230px,0.65fr)]">
          <Link
            to="/practice"
            className="group relative min-h-[270px] overflow-hidden rounded-[2rem] p-6 text-white shadow-[0_22px_50px_rgba(30,41,59,0.22)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_26px_58px_rgba(30,41,59,0.27)] md:p-8"
            style={{ background: "linear-gradient(135deg, #1E293B 0%, #304775 58%, #465FC2 100%)" }}
          >
            <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#FF5F65]/20" />
            <div className="relative z-10 flex h-full max-w-[58%] flex-col items-start justify-between">
              <span className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white/85">
                {scenarioCount > 0 ? `${scenarioCount} ${t("dash.scenariosShort")}` : t("dash.todayKicker")}
              </span>
              <h2 className="my-6 text-3xl font-extrabold leading-tight md:text-4xl">{t("dash.practice")}</h2>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FF5F65] px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-rose-950/15">
                {t("dash.todayStart")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
            <div className="absolute right-[7%] top-1/2 h-36 w-36 -translate-y-1/2 rotate-6 rounded-[2.5rem] border border-white/25 bg-white/15" />
            <div className="absolute right-[11%] top-1/2 flex h-32 w-32 -translate-y-1/2 -rotate-3 items-center justify-center rounded-[2.25rem] border border-white/30 bg-white/20 backdrop-blur-sm">
              <MessageCircle className="h-12 w-12" />
            </div>
            <span className="absolute bottom-[18%] right-[6%] h-6 w-6 rounded-full border-4 border-[#304775] bg-[#FFB84D]" />
          </Link>

          <div className="grid grid-cols-3 gap-2 lg:grid-cols-1">
            {statPills.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.25 }}
                className="flex min-h-[84px] items-center gap-3 rounded-2xl p-3 lg:px-5"
                style={{ backgroundColor: s.bg }}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white" style={{ backgroundColor: s.color }}><s.icon className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <CountUp value={s.value} suffix={s.suffix} className="block text-xl font-extrabold tabular-nums" style={{ color: s.color }} />
                  <span className="block truncate text-[11px] font-bold text-muted-foreground">{s.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {ACTIONS.slice(1).map(({ key, to, icon: Icon, accent, bg }, index) => (
            <motion.div key={key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + index * 0.04 }}>
              <Link to={to} className="group flex min-h-[132px] flex-col justify-between rounded-[1.6rem] border border-white/80 p-4 transition duration-200 hover:-translate-y-1 hover:shadow-lg" style={{ backgroundColor: bg }}>
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm" style={{ backgroundColor: accent }}><Icon className="h-5 w-5" /></div>
                  <ArrowRight className="h-4 w-4 -rotate-45 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: accent }} />
                </div>
                <span className="max-w-[10rem] text-sm font-extrabold leading-snug text-[#1E293B]">{t(`dash.${key}`)}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
